import React, { useEffect, useRef, useState } from "react";

import {
  Button,
  Typography,
  Box,
  Grid,
  styled,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import ToastSnackbar, { SnackbarHandle } from "../../common/ToastNotification";

import HomeIcon from "@mui/icons-material/Home";

import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";

import { ListFilteredStudents } from "../../api/Students-Management/add-view-students";
import { Get as GetClass } from "../../api/Control-Settings/manage-class";
import { GetSections } from "../../api/Exams-Management/manage-section";

import { useForm } from "react-hook-form";
import ControlledSelect from "../../common/ControlledComponents/ControlledSelect";

import { getCurrentUser } from "../../service/authService";
import { flattenObject } from "./helper";

import StudentFormRow from "./StudentFormRow";
import { BulkUpdateStudent } from "../../api/Students-Management/bulk-update";

const MyCustomButton = styled(Button)(({ theme }) => ({
  fontFamily: "Motiva Sans Bold",
  fontSize: "0.80rem",
  fontWeight: "700",
  border: "1px solid #edf3f5",
  borderRadius: "4px",
  background: "#2e186a",
  boxShadow: "0 16px 30px rgb(23 31 114 / 20%)",
  marginTop: "0rem",
  "&:hover": {
    color: "#fff",
    border: "1px solid rgb(255, 130, 92)",
    backgroundColor: "rgb(255, 130, 92)",
  },
}));

const BulkUpdate = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState<any>([]);

  const snackbarRef = useRef<SnackbarHandle>(null);

  const [classList, setClassList] = useState<any>([]);
  const [sectionList, setSectionList] = useState<any>([]);

  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);

  const [loggedInUser, setLoggedInUser] = useState("");

  const [flatStudentArray, setFlatStudentArray] = useState<any>([]);

  const [dirtyMap, setDirtyMap] = useState<any>([]);
  const [updating, setUpdating] = useState<boolean>(false);
  const [dirtyVersion, setDirtyVersion] = useState(0);

  useEffect(() => {
    if (applications && applications.length > 0) {
      const flattenStudents = applications.map((student: any) =>
        flattenObject(student)
      );

      setFlatStudentArray(flattenStudents);
    }
  }, [applications]);

  useEffect(() => {
    const { username } = getCurrentUser();
    setLoggedInUser(username);
  }, []);

  const {
    handleSubmit,
    reset,
    formState: { errors },
    control,
  } = useForm({
    defaultValues: {
      admission_id: "",
      name: "",
      class_id: "",
      section_id: "",
      roll_number: "",
    },
    mode: "onTouched",
  });

  const {
    control: controlBulkUpdate,
    handleSubmit: handleSubmitBulkUpdate,
    register,
  } = useForm({
    defaultValues: {
      students: applications, // full array of nested students
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [
          //students_records,
          // academic_records,
          classes_records,
          section_records,
        ] = await Promise.all([
          //GetStudents(),
          // GetAcademicsRecord(),
          GetClass(),
          GetSections(),
        ]);

        if (classes_records && classes_records.result.documents?.length > 0) {
          setClassList(classes_records.result.documents);
        }
        if (section_records && section_records.result.documents?.length > 0) {
          setSectionList(section_records.result.documents);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        snackbarRef.current?.showSnackbar(`Error fetching data`, "error");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    // This will now trigger reliably
    console.log("dirtyMap changed!", dirtyMap);
  }, [dirtyVersion]);

  const onResetHandler = () => {
    reset({
      admission_id: "",
      name: "",
      class_id: "",
      section_id: "",
      roll_number: "",
    });
    setApplications([]);
    setDirtyVersion(0);
  };

  const JSONParseStudentObject = (student_string: any) => {
    try {
      const student_json_parsed = student_string.map((stud: any) => ({
        ...stud,
        transport_details: JSON.parse(stud.transport_details),
        personal_details: JSON.parse(stud.personal_details),
        guardian_details: JSON.parse(stud.guardian_details),
        father_details: JSON.parse(stud.father_details),
        mother_details: JSON.parse(stud.mother_details),
        previous_school: JSON.parse(stud.previous_school),
      }));

      return student_json_parsed;
    } catch (error) {
      console.log("Error JSON parsing student object");
      console.log(error);
      return [];
    }
  };

  const HandleFetchStudents = async (data: any) => {
    try {
      console.log("Go Clicked for Class");
      console.log(data);
      setFetching(true);
      setApplications([]);
      setDirtyVersion(0);

      const { admission_id, class_id, section_id, roll_number } = data;

      const payload = {
        admission_id,
        class_id,
        section_id,
        roll_number: parseInt(roll_number),
      };
      console.log(payload);
      const students_records = await ListFilteredStudents(payload);
      if (students_records && students_records.result.documents?.length > 0) {
        const student_string = students_records.result.documents;
        const student_json_parsed = JSONParseStudentObject(student_string);
        const student_json_parsed_sorted = [...student_json_parsed].sort(
          (a: any, b: any) => a.roll_number - b.roll_number
        );
        setApplications(student_json_parsed_sorted);
      } else {
        snackbarRef.current?.showSnackbar(`No Record Found`, "info");
      }
    } catch (error) {
      console.log(error);
      snackbarRef.current?.showSnackbar(`Some Error occured`, "error");
    } finally {
      setFetching(false);
    }
  };

  const handleStudentChange = (id: any, updatedData: any) => {
    setApplications((prev: any) =>
      prev.map((s: any) => (s.student_id === id ? { ...s, ...updatedData } : s))
    );

    // Extract only fields we care about
    const { class_id, section_id, roll_number } = updatedData;

    // Find original student for comparison
    const original = applications.find((s: any) => s.student_id === id);
    if (!original) return;

    // Build changedFields if any of the 3 fields changed
    const changedFields: any = {};
    if (original.class_id !== class_id) {
      changedFields.class_id = class_id;
      // changedFields.class_name = class_name;
    }

    if (original.section_id !== section_id) {
      changedFields.section_id = section_id;
      // changedFields.section_name = section_name;
    }

    if (original.roll_number !== roll_number)
      changedFields.roll_number = roll_number;

    // Only update dirtyMap if something actually changed
    if (Object.keys(changedFields).length > 0) {
      setDirtyMap((prev: any) => ({
        ...prev,
        [id]: {
          ...(prev[id] || {}),
          ...changedFields,
        },
      }));

      setDirtyVersion((v) => v + 1); // 🚀 bump version
    }
  };

  const handleSaveChanges = async () => {
    console.log(dirtyMap);

    try {
      setUpdating(true);
      const updated_array = Object.entries(dirtyMap).map(([key, value]) => {
        let class_name = undefined;
        let section_name = undefined;
        let _roll_number = undefined;

        //@ts-ignore
        if (value?.section_id) {
          const sectionItem = sectionList.find(
            //@ts-ignore
            (item: any) => item.section_id === value.section_id
          );
          section_name = sectionItem?.name;
        }

        //@ts-ignore
        if (value?.class_id) {
          const classItem = classList.find(
            //@ts-ignore
            (item: any) => item.class_id === value.class_id
          );
          class_name = classItem.name;
        }

        //@ts-ignore
        if (value?.roll_number) {
          //@ts-ignore
          const parsed = parseInt(value?.roll_number);
          _roll_number = isNaN(parsed) ? 0 : parsed;
          value = {
            ...value,
            roll_number: _roll_number,
          };
        }

        return {
          //@ts-ignore
          ...value,
          id: key,
          ...(class_name ? { class_name } : {}),
          ...(section_name ? { section_name } : {}),
        };
      });

      console.log(updated_array);
      const payload = {
        user: loggedInUser || "testuser",
        records: updated_array,
      };

      const response = await BulkUpdateStudent(payload);
      if (response) {
        if (response.status === "SUCCESS")
          snackbarRef.current?.showSnackbar(`${response.message}`, "success");

        if (response.status === "FAIL")
          snackbarRef.current?.showSnackbar(`${response.message}`, "warning");
      } else {
        snackbarRef.current?.showSnackbar(`Some Error. Not Updated`, "warning");
      }
    } catch (error) {
      snackbarRef.current?.showSnackbar(
        `Some Exception Occured. Not Updated`,
        "warning"
      );
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      <ToastSnackbar ref={snackbarRef} />

      <Box
        display={"flex"}
        flexDirection={"column"}
        p={2}
        height="auto"
        // style={{ minHeight: "100vh" }}
      >
        <Box
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"space-between"}
        >
          <Box
            display={"flex"}
            flexDirection={"row"}
            justifyContent={"flex-start"}
            p={2}
            pb={0}
            gap={1}
          >
            <MyCustomButton
              variant="contained"
              startIcon={<ArrowLeftIcon />}
              onClick={() => navigate("..")}
            >
              Go Back
            </MyCustomButton>
            <MyCustomButton
              variant="contained"
              startIcon={<HomeIcon />}
              onClick={() => navigate("/home")}
            >
              Home
            </MyCustomButton>
          </Box>
        </Box>

        <Grid container direction="column" mt={4}>
          <Grid item xs={12}>
            <Box
              display={"flex"}
              flexDirection={"column"}
              p={2}
              pt={0}
              height="auto"
              justifyContent={"center"}
              alignItems={"center"}
              mt={-4}
            >
              <Typography variant="h5" alignSelf={"center"} mb={1}>
                <strong>Bulk Update Student Data</strong>
              </Typography>
              {loading ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="100px"
                >
                  <CircularProgress />
                </Box>
              ) : (
                <form
                  onSubmit={handleSubmit(HandleFetchStudents)}
                  onReset={onResetHandler}
                >
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    justifyContent={"space-evenly"}
                    gap={2}
                    width="90vw"
                  >
                    {/* <ControlledTextField
                    name="admission_id"
                    control={control}
                    errors={errors}
                    label="Admission Id"
                    sx={{ width: "30%", mt: 0 }}
                  /> */}
                    <ControlledSelect
                      name={`class_id`}
                      control={control}
                      errors={errors}
                      label="Class"
                      options={classList.map((item: any) => ({
                        value: item.class_id,
                        label: item.name,
                      }))}
                      sx={{ width: "30%" }}
                    />
                    <ControlledSelect
                      name={`section_id`}
                      control={control}
                      errors={errors}
                      label="Section"
                      options={sectionList.map((item: any) => ({
                        value: item.section_id,
                        label: item.name,
                      }))}
                      sx={{ width: "30%" }}
                    />

                    <MyCustomButton
                      variant="contained"
                      type="submit"
                      startIcon={
                        fetching ? <CircularProgress size={20} /> : null
                      }
                      disabled={fetching}
                      sx={{
                        alignSelf: "center",
                        height: "70%",
                        width: "20%",
                      }}
                    >
                      {fetching ? "" : "Fetch Students"}
                    </MyCustomButton>
                    <MyCustomButton
                      variant="contained"
                      type="reset"
                      sx={{
                        alignSelf: "center",
                        height: "70%",
                        width: "10%",
                      }}
                    >
                      Clear
                    </MyCustomButton>
                  </Box>
                </form>
              )}

              {applications && applications.length > 0 && (
                <MyCustomButton
                  variant="contained"
                  type="reset"
                  sx={{
                    alignSelf: "center",
                    height: "70%",
                    width: "10%",
                    marginTop: 2,
                  }}
                  onClick={handleSaveChanges}
                  startIcon={
                    updating ? <CircularProgress size={20} /> : undefined
                  }
                  disabled={
                    updating ? updating : dirtyVersion > 0 ? false : true
                  }
                >
                  {updating ? "...Updating" : "Save Changes"}
                </MyCustomButton>
              )}
              <Box
                sx={{
                  maxHeight: "65vh", // Adjust this value as needed
                  overflowY: "auto",
                  "&::-webkit-scrollbar": {
                    width: "8px", // Width of the scrollbar
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#f1f1f1", // Background of the scrollbar track
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888", // Color of the scroll thumb
                    borderRadius: "10px", // Rounded corners
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#555", // Darker color on hover for the thumb
                  },
                }}
              >
                {applications.map((student: any) => (
                  <Box key={student.student_id} width={"90vw"}>
                    <StudentFormRow
                      student={student}
                      onChange={handleStudentChange}
                      class_list={classList}
                      sections={sectionList}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default React.memo(BulkUpdate);
